/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Divider, Paper, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import CustomAccordion from "components/CustomAccordion";
import ModalContainer from "components/ModalContainer";
import ModalLayout from "components/ModalLayout";
import useOpenClose from "hooks/useOpenClose";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getSingleUser, unblockCredit } from "services/miscService";
import configData from "config/config.json";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";

export const getNestedValue = (obj, path, defaultValue = null) => {
  if (!obj || !path) return defaultValue;

  //const normalizePath = path.replace(/\[(\w+)\]/g, '.$1');
  const val = path.split(".").reduce((acc, key) => {
    if (acc && acc[key] !== undefined) {
      return acc[key];
    }
    return defaultValue;
  }, obj);

  // Handle boolean values explicitly
  if (typeof val === "boolean") {
    return val ? "true" : "false";
  }

  return val ?? defaultValue; // Ensure nullish values are replaced with defaultValue
};

const styles = makeStyles((theme) => ({
  root: { display: "flex" },
  detailContainer: { background: "#fff", borderRadius: 12, width: "100%" },
  row: { display: "flex", justifyContent: "space-between" },
  setcionContainer: {
    display: "flex",
    gap: theme.spacing(2),
    flexFlow: "row wrap",
  },
  subSecContainer: { display: "flex", flexFlow: "column wrap" },
  modalOutCont: { display: "flex", flexFlow: "column" },
  modalInnerCont: { display: "flex", justifyContent: "space-between" },
}));

const groupedDetails = {
  // User Details
  User_Details: [
    { id: "_id", label: "DB Id (User Id)" },
    { id: "rgsPlayerId", label: "RGS Player Id" },
    { id: "externalPlayerId", label: "External ID" },
    { id: "platformId", label: "Platform Id" },
    { id: "operatorId", label: "Operator Id" },
    { id: "brandId", label: "Brand Id" },
    { id: "countryCode", label: "Country Code" },
    { id: "currency", label: "Currency" },
    { id: "language", label: "language" },
    { id: "lastLoginDate", label: "Last Login Date" },

    { id: "balance", label: "Balance" },
    { id: "pbfCoin", label: "PBF Coins" },
    { id: "creditPendingSince", label: "Credit Pending Since" },
  ],

  // User_Team_Details
  User_Team_Details: [
    { id: "team.name", label: "Team Name" },
    { id: "team.autoName", label: "Auto Name" },
    { id: "team.attackRating", label: "Attack Rating" },
    { id: "team.defenseRating", label: "Defense Rating" },
    { id: "team.creativityRating", label: "Creativity Rating" },
    { id: "team.overallRating", label: "OVR" },
    { id: "team.chemistry", label: "OVR Chemistry Boost" },
    { id: "team.chemistryPercentage", label: "Chemistry Percentage" },
    { id: "team.totalTeamChemistry", label: "Total Chemistry" },
    { id: "team.formation", label: "Formation" },
    { id: "team.players", type: "modal", label: "See User's Players" }, // added property 'type' so that we can show button and open modal on click

    { id: "team.badge", label: "Badge" },
    { id: "team.kit", label: "Kit" },
    { id: "team.kitColor", label: "Kit Color" },
  ],

  // User Settings
  User_Settings: [
    { id: "settings.oddsDisplay", label: "Odds Display" },
    { id: "settings.audio", label: "Audio" },
  ],

  // Division Info
  Current_Division_Info: [
    { id: "leagueInfo.currentLeague", label: "Division" },
    { id: "leagueInfo.currentMilestone", label: "Milestone" },
    { id: "leagueInfo.currentStep", label: "Step" },
    { id: "leagueInfo.initialOvr", label: "Initial OVR" },
    { id: "goldStars", label: "Gold Stars" },
    { id: "seasonPoints", label: "Season Points" },
  ],

  FTUE_Info: [
    { id: "team.welcomePackNationality", label: "Selected FTUE Nationality" },
    { id: "team.selectedWelcomePack._id", label: "Welcome Pack ID" },
    {
      id: "team.selectedWelcomePack.teamNameExcel",
      label: "Welcome Team Name Excel",
    },
    { id: "team.selectedPlayStyle", label: "Play Style" },
  ],

  Weekly_Leaderboard: [
    { id: "weeklyLeaderboard.poolId", label: "Pool ID" },
    { id: "weeklyLeaderboard.leagueType", label: "Current League" },
    { id: "weeklyLeaderboard.currentRank", label: "Current Rank" },
    { id: "weeklyLeaderboard.weeklyXP", label: "Weekly XP" },
    { id: "weeklyLeaderboard.cycleNumber", label: "Current cycle" },
    { id: "weeklyLeaderboard.seasonId", label: "Current Season ID" },

    { id: "weeklyLeaderboard.lastWeekPosition", label: "Last week rank" },
    { id: "weeklyLeaderboard.lastWeekResult", label: "Last week result" },
    { id: "_id", type: "viewLeaderboard", label: "View Leaderboard" },
  ],
};

const UserDetails = () => {
  const classes = styles();
  const { search } = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [sectionDetails, setSectionDetails] = React.useState({});
  const { isOpen, extraData, open, close, updateExtra } = useOpenClose<{
    title?: string;
    items?: any[];
  }>();
  const [userInfo, setUserInfo] = React.useState({});
  const { showSnackbar } = useSnackbar();

  const userId = new URLSearchParams(search).get("id") || "";

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getSingleUser(userId);

      setUserInfo((prev) => ({
        ...prev,
        ...(resp?.data ?? {}),
      }));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error?.message, "error");
    }
  };

  const handleUnblock = async () => {
    try {
      await unblockCredit(userId);
      showSnackbar("Credit block cleared", "success");
      await fetchData();
    } catch (error) {
      showSnackbar(error?.message || "Failed to clear credit block", "error");
    }
  };

  const openPlayersModal = (data, columId) => {
    updateExtra({
      title: "User's Team Details",
      items: data?.map((el, index) => {
        const section = Object.keys(el)
          ?.filter((key) => el[key] !== null && typeof el[key] !== "object")
          ?.map((key) => ({
            id: `${columId}.${index}.${key}`,
            label: key
              .replace(/_/g, " ") // remove _ from the string
              .replace(/([A-Z])/g, " $1") // Split camelCase
              .replace(/^./, (str) => str.toUpperCase()), // make first letter capitalise
          }));
        return section;
      }),
    });
    open();
  };

  const viewUserLeaderboard = (userId: any) => {
    if (userId) {
      navigate(`${configData.routes.weeklyLeaderboard.view}?userId=${userId}`);
    }
  };

  useEffect(() => {
    if (userId) fetchData();

    // need to find an alternative way if there are more info need to be added
    const _sectionDetails = { ...groupedDetails };
    _sectionDetails.User_Team_Details.forEach((el) => {
      if (el.id === "team.players") el["onClick"] = openPlayersModal;
    });
    _sectionDetails.Weekly_Leaderboard.forEach((el) => {
      if (el.type === "viewLeaderboard") el["onClick"] = viewUserLeaderboard;
    });
    setSectionDetails(_sectionDetails);
    setLoading(false);
  }, []);

  return (
    <ContentShell loading={loading} title="User Details" showBackButton={true}>
      <div className={classes.root}>
        {userInfo ? (
          <Box className={classes.detailContainer}>
            {(userInfo as any).creditPendingSince && sessionHelper.hasRole(PermittedRoles.ALL_USERS.UNBLOCK_CREDIT_ROLES) ? (
              <Box style={{ padding: 14 }}>
                <Button onClick={handleUnblock} variant="contained" color="primary">
                  Unblock Credit
                </Button>
              </Box>
            ) : null}
            {Object.entries(sectionDetails).map(([sectionTitle, columns]) => {
              return (
                <Paper
                  elevation={4}
                  key={sectionTitle}
                  style={{ margin: 14, padding: 14 }}
                >
                  <Typography variant="h6" fontWeight={"bold"} gutterBottom>
                    {sectionTitle?.replace(/_/g, " ")}
                  </Typography>
                  <Box className={classes.setcionContainer}>
                    {(columns as any).map((column: any) => {
                      const value = getNestedValue(userInfo, column?.id);
                      return (
                        <Box
                          key={column.id}
                          className={classes.subSecContainer}
                        >
                          <Paper
                            elevation={2}
                            sx={{ p: 3, gap: 10, height: "100%" }}
                          >
                            {column?.type === "modal" ? (
                              <>
                                <Button
                                  onClick={() =>
                                    column?.onClick(value, column?.id)
                                  }
                                  variant="outlined"
                                >
                                  {column.label || column.id}
                                </Button>
                              </>
                            ) : column?.type === "viewLeaderboard" ? (
                              <>
                                <Button
                                  onClick={() => column?.onClick(value)}
                                  variant="contained"
                                  color="primary"
                                >
                                  {column.label || column.id}
                                </Button>
                              </>
                            ) : (
                              <>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  {column.label || column.id}
                                </Typography>
                                <Typography fontWeight={"bold"} variant="body2">
                                  {value ?? "--"}
                                </Typography>
                              </>
                            )}
                          </Paper>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              );
            })}
            <ModalContainer openModel={isOpen} handleClose={close}>
              <ModalLayout
                isLargeContainer={true}
                hasPadding={true}
                padding={35}
              >
                <Box style={{ gap: 5, display: "flex", flexFlow: "column" }}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    {extraData?.title || ""}
                  </Typography>
                  {extraData?.items?.map((item, index) => {
                    return (
                      <CustomAccordion key={index} id={"Player " + (index + 1)}>
                        <Box className={classes.modalOutCont}>
                          {item?.map((column, _) => {
                            const value = getNestedValue(userInfo, column?.id);
                            return (
                              <Box key={column?.id}>
                                <Box className={classes.modalInnerCont}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    {column.label || column.id}
                                  </Typography>
                                  <Typography
                                    fontWeight={"bold"}
                                    variant="body2"
                                  >
                                    {value ?? "--"}
                                  </Typography>
                                </Box>
                                {<Divider />}
                              </Box>
                            );
                          })}
                        </Box>
                      </CustomAccordion>
                    );
                  })}
                </Box>
              </ModalLayout>
            </ModalContainer>
          </Box>
        ) : (
          <Box style={{ margin: "auto" }}>No user data available!</Box>
        )}
      </div>
    </ContentShell>
  );
};

export default UserDetails;
